import { GraphQLOptionsV6, GraphQLResult } from '@aws-amplify/api-graphql';
import { DocumentNode, GraphQLError } from 'graphql';
import { useState } from 'react';
import api from '../services/api';

type Options<TResult> = Omit<GraphQLOptionsV6<TResult, string>, 'query'>;

type UseMutation<TResult> = [
  mutation: (opts: Options<TResult>) => Promise<void>,
  {
    data?: TResult;
    error?: GraphQLError[];
    loading: boolean;
  },
];

interface Hooks<TResult> {
  onCompleted?: (data: TResult) => void;
  onError?: (error: GraphQLError[]) => void;
}

function useMutation<TResult>(
  query: string | DocumentNode,
  { onCompleted, onError, ...options }: Options<TResult> & Hooks<TResult> = {},
): UseMutation<TResult> {
  const [data, setData] = useState<TResult>();

  const [error, setError] = useState<GraphQLError[]>();

  const [loading, setLoading] = useState(false);

  const mutation = async (opts: Options<TResult>) => {
    try {
      setLoading(true);

      const result = (await api.graphql({
        ...options,
        ...opts,
        query,
      })) as GraphQLResult<TResult>;

      if (result.data) {
        setData({
          ...(result.data as TResult),
        });

        if (onCompleted) {
          onCompleted(result.data);
        }
      }

      if (result.errors) {
        setError(result.errors);

        if (onError) {
          onError(result.errors);
        }
      }
    } catch (e) {
      setError([]);
    } finally {
      setLoading(false);
    }
  };

  return [
    mutation,
    {
      data,
      error,
      loading,
    },
  ];
}

export default useMutation;
